<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import { http } from '@/helpers/easyindustriaapi/config'

export default {
  components: { Layout, PageHeader },
  data() {
    return {
      titleBody: 'Rel. Fluxo de Caixa',
      items: [
        { text: 'Financeiro', href: '/financeiro' },
        { text: 'Rel. Fluxo de Caixa', active: true }
      ],
      currentEmpresa: null,
      isLoading: false,
      error: null,
      pdfSrc: null,
      showFilters: true,
      filters: {
        ano: new Date().getFullYear(),
        mes_inicio: 1,
        mes_fim: null
      },
      mesesNomes: [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ]
    }
  },
  created() {
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'))
  },
  computed: {
    mesesOptions() {
      return Array.from({ length: 12 }, (_, i) => ({
        text: this.mesesNomes[i] || `Mês ${i + 1}`,
        value: i + 1
      }))
    },
    mesFimOptions() {
      if (!this.filters.mes_inicio) {
        return this.mesesOptions
      }
      // Filtrar apenas meses >= mes_inicio e limitar a 6 meses
      const mesInicio = this.filters.mes_inicio
      const mesesDisponiveis = []
      
      for (let i = 0; i < 6; i++) {
        let mes = mesInicio + i
        if (mes > 12) {
          mes = mes - 12
        }
        mesesDisponiveis.push({
          text: this.mesesNomes[mes - 1] || `Mês ${mes}`,
          value: mes
        })
      }
      
      return [{ text: '-- Selecione --', value: null }, ...mesesDisponiveis]
    },
    mesFimMaximo() {
      if (!this.filters.mes_inicio) {
        return null
      }
      // Calcular o mês máximo (6 meses a partir do inicial)
      let mesMax = this.filters.mes_inicio + 5
      if (mesMax > 12) {
        mesMax = mesMax - 12
      }
      return mesMax
    }
  },
  methods: {
    async generateReport() {
      if (!this.currentEmpresa || !this.currentEmpresa.id) {
        this.error = 'Empresa não selecionada'
        return
      }

      // Validação: mes_fim deve ser >= mes_inicio
      if (this.filters.mes_fim && this.filters.mes_fim < this.filters.mes_inicio) {
        this.error = 'O mês final deve ser maior ou igual ao mês inicial'
        return
      }

      // Validação: intervalo máximo de 6 meses
      if (this.filters.mes_fim) {
        let quantidadeMeses = 0
        if (this.filters.mes_fim >= this.filters.mes_inicio) {
          quantidadeMeses = this.filters.mes_fim - this.filters.mes_inicio + 1
        } else {
          quantidadeMeses = (12 - this.filters.mes_inicio + 1) + this.filters.mes_fim
        }
        
        if (quantidadeMeses > 6) {
          this.error = 'O intervalo entre o mês inicial e o mês final não pode ser maior que 6 meses.'
          return
        }
      }

      try {
        this.error = null
        this.isLoading = true
        this.pdfSrc = null

        const params = {
          empresa_id: this.currentEmpresa.id,
          ano: this.filters.ano,
          mes_inicio: this.filters.mes_inicio
        }

        // Adicionar mes_fim apenas se foi informado
        if (this.filters.mes_fim) {
          params.mes_fim = this.filters.mes_fim
        }

        const response = await http.get('/relatorios/fluxo-caixa', { 
          params,
          responseType: 'blob'
        })

        const contentType = response.headers?.['content-type'] || response.headers?.get?.('content-type')
        if (contentType && contentType.includes('application/pdf')) {
          this.setPdfFromBlob(response.data)
          this.showFilters = false
        } else {
          this.error = 'Não foi possível gerar o PDF do relatório.'
        }
      } catch (e) {
        console.error('Erro ao gerar relatório:', e)
        this.error = e.response?.data?.message || 'Erro ao gerar relatório. Verifique os filtros e tente novamente.'
      } finally {
        this.isLoading = false
      }
    },
    clearPdf() {
      if (this.pdfSrc && this.pdfSrc.startsWith('blob:')) {
        URL.revokeObjectURL(this.pdfSrc)
      }
      this.pdfSrc = null
      this.error = null
    },
    setPdfFromBlob(blob) {
      this.clearPdf()
      this.pdfSrc = URL.createObjectURL(blob)
    },
    onPrimaryButtonClick() {
      if (!this.showFilters) {
        // volta para filtros e limpa PDF
        this.showFilters = true
        this.clearPdf()
        this.isLoading = false
        this.error = null
        return
      }
      this.generateReport()
    },
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="titleBody" :items="items" />
    
    <!-- Filtros -->
    <div class="row" v-show="showFilters">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-card>
              <div class="row mb-3">
                <div class="col-md-4">
                  <b-form-group label="Ano:">
                    <b-form-input 
                      type="number" 
                      v-model="filters.ano" 
                      :min="2020" 
                      :max="2100"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <b-form-group label="Mês Inicial:">
                    <b-form-select 
                      v-model="filters.mes_inicio" 
                      :options="mesesOptions"
                    />
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <b-form-group label="Mês Final (opcional):">
                    <b-form-select 
                      v-model="filters.mes_fim" 
                      :options="mesFimOptions"
                    />
                    <small class="form-text text-muted">
                      <strong>Limite:</strong> O intervalo máximo permitido é de 6 meses.
                      <span v-if="mesFimMaximo">
                        Máximo: {{ mesesNomes[mesFimMaximo - 1] }} (6 meses a partir de {{ mesesNomes[filters.mes_inicio - 1] }}).
                      </span>
                      Se não informado, será calculado 6 meses a partir do mês inicial.
                    </small>
                  </b-form-group>
                </div>
              </div>
            </b-card>
          </div>
        </div>
      </div>
    </div>

    <!-- Botão de ação -->
    <div class="row">
      <div class="col-12 text-right mb-2">
        <b-button variant="primary" @click="onPrimaryButtonClick">
          {{ showFilters ? 'Gerar relatório' : 'Filtros...' }}
        </b-button>
      </div>
    </div>

    <!-- Mensagem de Erro -->
    <div class="row mt-3" v-if="error">
      <div class="col-12">
        <b-alert variant="danger" show>{{ error }}</b-alert>
      </div>
    </div>

    <!-- Loading -->
    <div class="row mt-3" v-if="isLoading">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <b-spinner label="Carregando..."></b-spinner>
            <div class="text-muted mt-2">Gerando relatório em PDF...</div>
          </div>
        </div>
      </div>
    </div>

    <!-- PDF Viewer -->
    <div class="row mt-3" v-if="pdfSrc && !isLoading">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="pdf-container">
              <iframe :src="pdfSrc" width="100%" height="820" style="border: none;"></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensagem quando não há PDF -->
    <div class="row mt-3" v-if="!pdfSrc && !isLoading && !error && !showFilters">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <p class="text-muted">Selecione os filtros e clique em "Gerar Relatório" para visualizar o PDF aqui.</p>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.table th {
  font-weight: 600;
  vertical-align: middle;
}

.table td {
  vertical-align: middle;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.bg-warning {
  background-color: #fff3cd !important;
}

.bg-info {
  background-color: #0dcaf0 !important;
}

.bg-success {
  background-color: #198754 !important;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.pl-4 {
  padding-left: 1.5rem !important;
}

.pdf-container {
  width: 100%;
  min-height: 820px;
}
</style>


